import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { TrendingUp, Users, PieChart, Timer } from 'lucide-react';

const Reports = () => {
  const reports = [
    {
      icon: <TrendingUp className="h-6 w-6 text-accent" />,
      title: "Monthly Effort & Cost Breakdown",
      description: "Clear breakdown of effort expended over the past 12 months with corresponding costs for each month.",
      image: "https://images.unsplash.com/photo-1748609160056-7b95f30041f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGFuYWx5dGljcyUyMGRhc2hib2FyZHxlbnwwfHx8fDE3NjA2MjQyNjJ8MA&ixlib=rb-4.1.0&q=85",
      benefits: ["Monthly cost analysis", "Trend identification", "Budget planning"]
    },
    {
      icon: <PieChart className="h-6 w-6 text-accent" />,
      title: "Department-wise Effort Reports",
      description: "Identify effort contributed by each department to specific projects. Make crucial decisions to realign resources.",
      benefits: ["Resource allocation", "Department insights", "Project realignment"]
    },
    {
      icon: <Users className="h-6 w-6 text-accent" />,
      title: "User-wise Effort Analysis",
      description: "Visually identify which team members dedicated the most time to specific projects for effective resource planning.",
      benefits: ["Individual tracking", "Performance insights", "Resource planning"]
    },
    {
      icon: <Timer className="h-6 w-6 text-accent" />,
      title: "Billable vs Non-Billable Reports",
      description: "Clear view of all time spent including demonstrations, in-house development, and other non-billable activities.",
      benefits: ["Time categorization", "Revenue optimization", "Accurate billing"]
    }
  ];

  return (
    <section id="reports" className="py-20 lg:py-32 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <Badge variant="secondary" className="mb-4 bg-accent/10 text-accent border-accent/20">
            Comprehensive Analytics
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-6">
            Powerful Reports &
            <span className="text-gradient-primary block">
              Analytics Dashboard
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Get deep insights into your team's productivity, project costs, and resource allocation with our comprehensive reporting suite
          </p>
        </div>

        {/* Reports Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {reports.map((report, index) => (
            <Card key={index} className="group hover-lift border-0 shadow-card bg-card/60 backdrop-blur-sm animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 rounded-lg bg-accent/10 group-hover:bg-accent/20 transition-colors duration-300">
                    {report.icon}
                  </div>
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    Report {String(index + 1).padStart(2, '0')}
                  </Badge>
                </div>
                <CardTitle className="text-xl font-heading font-semibold text-foreground group-hover:text-primary transition-colors">
                  {report.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {report.description}
                </p>
                
                {/* Report image if available */}
                {report.image && (
                  <div className="relative mb-4 rounded-lg overflow-hidden">
                    <img
                      src={report.image}
                      alt={report.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-transparent to-transparent"></div>
                  </div>
                )}
                
                {/* Benefits */}
                <div className="space-y-2">
                  {report.benefits.map((benefit, benefitIndex) => (
                    <div key={benefitIndex} className="flex items-center space-x-2 text-sm">
                      <div className="w-1.5 h-1.5 bg-accent rounded-full flex-shrink-0"></div>
                      <span className="text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Statistics */}
        <div className="grid sm:grid-cols-3 gap-8 animate-fade-in">
          <div className="text-center space-y-2">
            <div className="text-3xl font-heading font-bold text-accent">12+</div>
            <div className="text-sm text-muted-foreground">Report Types</div>
          </div>
          <div className="text-center space-y-2">
            <div className="text-3xl font-heading font-bold text-accent">Real-time</div>
            <div className="text-sm text-muted-foreground">Data Updates</div>
          </div>
          <div className="text-center space-y-2">
            <div className="text-3xl font-heading font-bold text-accent">100%</div>
            <div className="text-sm text-muted-foreground">Accurate Tracking</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reports;